import "@/App.css";
import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { DataProvider } from "@/context/VelvetContext";
import AppShell from "@/components/layout/AppShell";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import Customers from "@/pages/Customers";
import Leads from "@/pages/Leads";
import Inbox from "@/pages/Inbox";
import Squarespace from "@/pages/Squarespace";
import Products from "@/pages/Products";
import Quotations from "@/pages/Quotations";
import QuotationBuilder from "@/pages/QuotationBuilder";
import QuotationDetail from "@/pages/QuotationDetail";
import QuotationPDF from "@/pages/QuotationPDF";
import Purchasing from "@/pages/Purchasing";
import Orders from "@/pages/Orders";
import MyDeliveries from "@/pages/MyDeliveries";
import DesignerBoard from "@/pages/DesignerBoard";
import Collections from "@/pages/Collections";
import CollectionDetail from "@/pages/CollectionDetail";
import ErrorBoundary from "@/components/ErrorBoundary";
import { AuthLoading, AuthError } from "@/components/AuthFallback";

const ROLE_HOME = {
  admin: '/dashboard',
  manager: '/dashboard',
  sales: '/dashboard',
  designer: '/production',
  driver: '/my-deliveries',
  purchasing: '/dashboard',
};

// Hard ceilings for loading states so the app can NEVER show a blank/indefinite spinner.
const LOADING_MAX_MS = 8000;
const PROFILE_WAIT_MAX_MS = 4000;

function RequireAuth({ children, allow }) {
  const { session, profile, loading, authError, bootstrapId, retryAuth, clearSessionAndLogout } = useAuth();
  const [loadingTimedOut, setLoadingTimedOut] = React.useState(false);
  const [profileTimedOut, setProfileTimedOut] = React.useState(false);

  // Reset on each bootstrap run (initial mount and every retry), so timers
  // start fresh and the error screen doesn't sticky across retries.
  React.useEffect(() => {
    setLoadingTimedOut(false);
    if (loading) {
      const t = setTimeout(() => setLoadingTimedOut(true), LOADING_MAX_MS);
      return () => clearTimeout(t);
    }
  }, [loading, bootstrapId]);

  React.useEffect(() => {
    setProfileTimedOut(false);
    if (session && !profile && !authError) {
      const t = setTimeout(() => setProfileTimedOut(true), PROFILE_WAIT_MAX_MS);
      return () => clearTimeout(t);
    }
  }, [session, profile, authError, bootstrapId]);

  // Explicit unrecoverable auth error → visible retry screen (never blank).
  if (authError) {
    return (
      <AuthError
        message={authError.message}
        onRetry={retryAuth}
        onSignOut={clearSessionAndLogout}
      />
    );
  }

  if (loading) {
    if (loadingTimedOut) {
      return (
        <AuthError
          message="Verifying your session is taking longer than usual."
          onRetry={retryAuth}
          onSignOut={clearSessionAndLogout}
        />
      );
    }
    return <AuthLoading message="Verifying your session…" />;
  }

  if (!session) return <Navigate to="/login" replace />;

  if (session && !profile) {
    if (!profileTimedOut) return <AuthLoading message="Loading your profile…" />;
    return (
      <AuthError
        message="We couldn't load your profile. Please retry or sign in again."
        onRetry={retryAuth}
        onSignOut={clearSessionAndLogout}
      />
    );
  }

  if (allow && !allow.includes(profile.role)) {
    return <Navigate to={ROLE_HOME[profile.role] || '/dashboard'} replace />;
  }
  return children;
}

function HomeRedirect() {
  const { session, profile, loading, authError, recoveryPending, retryAuth, clearSessionAndLogout } = useAuth();
  // If Supabase told us this session was born from a recovery/invite link,
  // never send the user to the dashboard — they must set a password first.
  if (recoveryPending) return <Navigate to="/auth/callback" replace />;
  if (authError) {
    return (
      <AuthError message={authError.message} onRetry={retryAuth} onSignOut={clearSessionAndLogout} />
    );
  }
  if (loading) return <AuthLoading message="Preparing Velvet OS…" />;
  if (!session) return <Navigate to="/login" replace />;
  if (!profile) return <AuthLoading message="Loading your profile…" />;
  return <Navigate to={ROLE_HOME[profile.role] || '/dashboard'} replace />;
}

// Runs on every navigation. Two safety nets:
//   1) If the URL contains a recovery/invite auth fragment and we're NOT on /auth/callback yet,
//      forward there preserving the hash so tokens can be consumed by the right screen.
//   2) If the sticky recoveryPending flag is set (PASSWORD_RECOVERY event fired), keep the user
//      on /auth/callback until they save a new password. Blocks every protected route.
function RecoveryGuard({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { recoveryPending } = useAuth();

  React.useEffect(() => {
    const hash = window.location.hash || '';
    const search = window.location.search || '';
    const looksLikeAuthFragment =
      /(?:^|&|#)(access_token|refresh_token|type=recovery|type=invite|error_description)=/.test(hash) ||
      /(?:^|&|\?)(code|type=recovery|type=invite|error_description)=/.test(search);
    if (looksLikeAuthFragment && location.pathname !== '/auth/callback') {
      navigate({ pathname: '/auth/callback', search, hash }, { replace: true });
      return;
    }
    if (recoveryPending && location.pathname !== '/auth/callback') {
      navigate('/auth/callback', { replace: true });
    }
  }, [location.pathname, location.search, location.hash, recoveryPending, navigate]);

  return children;
}

function ProtectedShell({ children }) {
  return (
    <RequireAuth>
      <DataProvider>
        <AppShell>{children}</AppShell>
      </DataProvider>
    </RequireAuth>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <RecoveryGuard>
          <div className="App">
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/auth/callback" element={<AuthCallback />} />
              <Route path="/" element={<HomeRedirect />} />
              <Route path="/dashboard" element={<ProtectedShell><RequireAuth allow={['admin','manager','sales','purchasing']}><Dashboard /></RequireAuth></ProtectedShell>} />
              <Route path="/customers" element={<ProtectedShell><RequireAuth allow={['admin','manager','sales']}><Customers /></RequireAuth></ProtectedShell>} />
              <Route path="/leads" element={<ProtectedShell><RequireAuth allow={['admin','manager','sales']}><Leads /></RequireAuth></ProtectedShell>} />
              <Route path="/inbox" element={<ProtectedShell><RequireAuth allow={['admin','manager','sales']}><Inbox /></RequireAuth></ProtectedShell>} />
              <Route path="/squarespace" element={<ProtectedShell><RequireAuth allow={['admin','manager']}><Squarespace /></RequireAuth></ProtectedShell>} />
              <Route path="/products" element={<ProtectedShell><RequireAuth allow={['admin','manager','designer','sales','purchasing']}><Products /></RequireAuth></ProtectedShell>} />
              <Route path="/collections" element={<ProtectedShell><RequireAuth allow={['admin','manager','sales','purchasing']}><Collections /></RequireAuth></ProtectedShell>} />
              <Route path="/collections/:id" element={<ProtectedShell><RequireAuth allow={['admin','manager','sales','purchasing']}><CollectionDetail /></RequireAuth></ProtectedShell>} />
              <Route path="/quotations" element={<ProtectedShell><RequireAuth allow={['admin','manager','sales','designer']}><Quotations /></RequireAuth></ProtectedShell>} />
              <Route path="/quotations/new" element={<ProtectedShell><RequireAuth allow={['admin','manager','sales']}><QuotationBuilder /></RequireAuth></ProtectedShell>} />
              <Route path="/quotations/:id" element={<ProtectedShell><RequireAuth allow={['admin','manager','sales','designer']}><QuotationDetail /></RequireAuth></ProtectedShell>} />
              <Route path="/quotations/:id/pdf" element={<ProtectedShell><RequireAuth allow={['admin','manager','sales']}><QuotationPDF /></RequireAuth></ProtectedShell>} />
              <Route path="/orders" element={<ProtectedShell><RequireAuth allow={['admin','manager','sales']}><Orders /></RequireAuth></ProtectedShell>} />
              <Route path="/production" element={<ProtectedShell><RequireAuth allow={['designer','admin','manager']}><DesignerBoard /></RequireAuth></ProtectedShell>} />
              <Route path="/purchasing" element={<ProtectedShell><RequireAuth allow={['admin','manager','purchasing']}><Purchasing /></RequireAuth></ProtectedShell>} />
              <Route path="/my-deliveries" element={<ProtectedShell><RequireAuth allow={['driver','admin','manager']}><MyDeliveries /></RequireAuth></ProtectedShell>} />
              <Route path="*" element={<HomeRedirect />} />
            </Routes>
            <Toaster
              position="bottom-right"
              toastOptions={{
                style: {
                  background: "hsl(228 19% 22%)",
                  color: "hsl(60 20% 97%)",
                  border: "1px solid hsl(228 19% 22%)",
                  borderRadius: "2px",
                  fontFamily: "Manrope, sans-serif",
                },
              }}
            />
          </div>
          </RecoveryGuard>
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}
